import { memo, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Button } from "@design-system/Button/Button";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { useEntities } from "@hooks/useEntities";
import { ClassRow } from "../viewUtils";
import styles from "./ClassItemViewCell.module.scss";

function collectSubtreeIds(item: ClassRow): string[] {
    const ids = [item.id];
    for (const child of item.children ?? []) {
        ids.push(...collectSubtreeIds(child));
    }
    return ids;
}

export const TransactionsCell = memo(function TransactionsCell({
    row,
    table,
}: CellContext<ViewTableFeatures, ClassRow, unknown>) {
    const item = row.original;
    // A row with children shows the subtree total regardless of expand state:
    // a parent can legitimately have 0 direct transactions while its
    // descendants hold them. Leaves show their own count.
    const hasChildren = (item.children?.length ?? 0) > 0;
    const openGlobalTransactionViewDrawer = useGlobalTransactionViewDrawer();
    const tableMeta = table.options.meta as
        | { entityIds?: number[] }
        | undefined;
    const entities = useEntities();
    const entityIds = tableMeta?.entityIds?.length
        ? tableMeta.entityIds
        : entities.map((e) => e.id);

    const transactionsCount = hasChildren
        ? item.groupTransactionsCount
        : item.transactionsCount;

    const classIds = useMemo(
        () => (hasChildren ? collectSubtreeIds(item) : [item.id]),
        [hasChildren, item],
    );

    return (
        <ViewTableCell captureClicks>
            <Button
                variant="secondary"
                size="sm"
                className={styles.transactionsButton}
                onClick={(e) => {
                    e.stopPropagation();
                    openGlobalTransactionViewDrawer({
                        classes: classIds,
                        entityIds,
                    });
                }}
                disabled={transactionsCount <= 0}
            >
                {transactionsCount}
            </Button>
        </ViewTableCell>
    );
});
